import React, { useContext, useEffect, useState } from "react";
import { Link as RouterLink, useLocation } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";

import ListItem from "@material-ui/core/ListItem";
import ListItemText from "@material-ui/core/ListItemText";
import ListSubheader from "@material-ui/core/ListSubheader";
import Divider from "@material-ui/core/Divider";
import { Badge } from "@material-ui/core";
import DashboardOutlinedIcon from "@material-ui/icons/DashboardOutlined";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";
import SyncAltIcon from "@material-ui/icons/SyncAlt";
import SettingsOutlinedIcon from "@material-ui/icons/SettingsOutlined";
import PeopleAltOutlinedIcon from "@material-ui/icons/PeopleAltOutlined";
import ContactPhoneOutlinedIcon from "@material-ui/icons/ContactPhoneOutlined";
import AccountTreeOutlinedIcon from "@material-ui/icons/AccountTreeOutlined";
import QuestionAnswerOutlinedIcon from "@material-ui/icons/QuestionAnswerOutlined";
import ApartmentIcon from '@material-ui/icons/Apartment';
import WatchLaterIcon from '@material-ui/icons/WatchLater';
import LocalHospitalIcon from '@material-ui/icons/LocalHospital';
import CalendarMonthIcon from '@material-ui/icons/CalendarToday';
import NotificationsIcon from '@material-ui/icons/Notifications';
import ScheduleIcon from '@material-ui/icons/Schedule';
import AssignmentIcon from '@material-ui/icons/Assignment';
import BuildIcon from '@material-ui/icons/Build';
import MonetizationOnIcon from '@material-ui/icons/MonetizationOn';
import HistoryIcon from "@material-ui/icons/History";
import { i18n } from "../translate/i18n";
import { WhatsAppsContext } from "../context/WhatsApp/WhatsAppsContext";
import { AuthContext } from "../context/Auth/AuthContext";
import { Can } from "../components/Can";
import { hasConnectionWarning } from "../helpers/whatsAppState";

const useStyles = makeStyles((theme) => {
  const isDark = theme.palette.type === "dark";

  return {
    root: {
      color: isDark ? "#fff" : "rgba(0, 0, 0, 0.87)",
      "& .custom-icon": {
        display: "inline-flex",
        minWidth: 56,
        justifyContent: "center",
        color: isDark ? "rgba(255, 255, 255, 0.7)" : "rgba(0, 0, 0, 0.54)",
      },
      "&:hover": {
        backgroundColor: theme.palette.action.hover,
      },
    },
    active: {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.primary.contrastText,
      "& .custom-icon": {
        color: theme.palette.primary.contrastText,
      },
      "&:hover": {
        backgroundColor: theme.palette.primary.dark,
      },
    },
  };
});

function ListItemLink({ icon, primary, to, isActive, onClick }) {
  const classes = useStyles();
  const renderLink = React.useMemo(
    () => React.forwardRef((itemProps, ref) => <RouterLink to={to} ref={ref} {...itemProps} />),
    [to]
  );

  return (
    <li>
      <ListItem
        button
        component={renderLink}
        onClick={onClick}
        className={`${classes.root} ${isActive ? classes.active : ""}`}
      >
        {icon ? <span className="custom-icon">{icon}</span> : null}
        <ListItemText primary={primary} />
      </ListItem>
    </li>
  );
}

const MainListItems = (props) => {
  const { drawerClose } = props;
  const { whatsApps, loading } = useContext(WhatsAppsContext);
  const { user } = useContext(AuthContext);
  const location = useLocation();
  const [activePath, setActivePath] = useState(location.pathname);
  const connectionWarning = !loading && hasConnectionWarning(whatsApps);

  useEffect(() => {
    setActivePath(location.pathname);
  }, [location.pathname]);


  const handleItemClick = (path) => {
    setActivePath(path);
    drawerClose();
  };

  return (
    <div onClick={drawerClose}>
      <ListItemLink
        to="/"
        primary="Dashboard"
        icon={<DashboardOutlinedIcon />}
        isActive={activePath === "/"}
        onClick={() => handleItemClick("/")}
      />
      <ListItemLink
        to="/connections"
        primary={i18n.t("mainDrawer.listItems.connections")}
        icon={
          <Badge badgeContent={connectionWarning ? "!" : 0} color="error">
            <SyncAltIcon />
          </Badge>
        }
        isActive={activePath.startsWith("/connections")}
        onClick={() => handleItemClick("/connections")}
      />
      <ListItemLink
        to="/tickets"
        primary={i18n.t("mainDrawer.listItems.tickets")}
        icon={<WhatsAppIcon />}
        isActive={activePath.startsWith("/tickets")}
        onClick={() => handleItemClick("/tickets")}
      />

      <ListItemLink
        to="/contacts"
        primary={i18n.t("mainDrawer.listItems.contacts")}
        icon={<ContactPhoneOutlinedIcon />}
        isActive={activePath.startsWith("/contacts")}
        onClick={() => handleItemClick("/contacts")}
      />
      <ListItemLink
        to="/quickAnswers"
        primary={i18n.t("mainDrawer.listItems.quickAnswers")}
        icon={<QuestionAnswerOutlinedIcon />}
        isActive={activePath.startsWith("/quickAnswers")}
        onClick={() => handleItemClick("/quickAnswers")}
      />
      <ListItemLink
        to="/scheduledMessages"
        primary={"Programados"}
        icon={<ScheduleIcon />}
        isActive={activePath.startsWith("/scheduledMessages")}
        onClick={() => handleItemClick("/scheduledMessages")}
      />
      <ListItemLink
        to="/calendar"
        primary={"Calendario"}
        icon={<CalendarMonthIcon />}
        isActive={activePath.startsWith("/calendar")}
        onClick={() => handleItemClick("/calendar")}
      />
			<ListItemLink
        to="/billing"
        primary="Caja"
        icon={<MonetizationOnIcon />}
        isActive={activePath.startsWith("/billing")}
        onClick={() => handleItemClick("/billing")}
      />
			<ListItemLink
				to="/medical-history"
				primary="Historial Médico"
				icon={<HistoryIcon />}
        isActive={activePath.startsWith("/medical-history")}
        onClick={() => handleItemClick("/medical-history")}
			/>
      <ListItemLink
				to="/prescriptionTemplates"
				primary={"Plantillas de recetas"}
				icon={<BuildIcon />}
				isActive={activePath.startsWith("/prescriptionTemplates")}
				onClick={() => handleItemClick("/prescriptionTemplates")}
			/>
      <Can
        role={user.profile}
        perform="drawer-admin-items:view"
        yes={() => (
          <>
            <Divider />
            <ListSubheader inset>
              {i18n.t("mainDrawer.listItems.administration")}
            </ListSubheader>
            <ListItemLink
              to="/company"
              primary={i18n.t("mainDrawer.listItems.company")}
              icon={<ApartmentIcon />}
              isActive={activePath.startsWith("/company")}
              onClick={() => handleItemClick("/company")}
            />
            <ListItemLink
              to="/users"
              primary={i18n.t("mainDrawer.listItems.users")}
              icon={<PeopleAltOutlinedIcon />}
              isActive={activePath.startsWith("/users")}
              onClick={() => handleItemClick("/users")}
            />
            <ListItemLink
              to="/queues"
              primary={i18n.t("mainDrawer.listItems.queues")}
              icon={<AccountTreeOutlinedIcon />}
              isActive={activePath.startsWith("/queues")}
              onClick={() => handleItemClick("/queues")}
            />
            <ListItemLink
              to="/professionalTitles"
              primary={"Especialidades"}
              icon={<LocalHospitalIcon />}
              isActive={activePath.startsWith("/professionalTitles")}
              onClick={() => handleItemClick("/professionalTitles")}
            />
          </>
        )}
      />
      <Can
        role={user.profile}
        perform="settings-page:view"
        yes={() => (
          <>
            <Divider />
            <ListSubheader inset>
              Superadmin
            </ListSubheader>
            <ListItemLink
              to="/settings"
              primary={i18n.t("mainDrawer.listItems.settings")}
              icon={<SettingsOutlinedIcon />}
              isActive={activePath.startsWith("/settings")}
              onClick={() => handleItemClick("/settings")}
            />
          </>
        )}
      />
      {(user.profile === "doctor" || (user.profile === "admin" && user.isDoctor)) && (
        <>
          <Divider />
          <ListSubheader inset>
            Doctor
          </ListSubheader>
          <ListItemLink
            to="/prescriptions"
            primary={"Recetas"}
            icon={<AssignmentIcon />}
            isActive={activePath.startsWith("/prescriptions")}
            onClick={() => handleItemClick("/prescriptions")}
          />
          <ListItemLink
            to="/mySchedules"
            primary={"Mis horarios"}
            icon={<WatchLaterIcon />}
            isActive={activePath.startsWith("/mySchedules")}
            onClick={() => handleItemClick("/mySchedules")}
          />
          <ListItemLink
            to="/myReminders"
            primary={"Mis recordatorios"}
            icon={<NotificationsIcon />}
            isActive={activePath.startsWith("/myReminders")}
            onClick={() => handleItemClick("/myReminders")}
          />
        </>
      )}
    </div>
  );
};

export default MainListItems;
